import { Phone, Mail, Clock, ArrowRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { COMPANY } from "@/lib/data";
import { useContent } from "@/lib/content-context";

const HERO = "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1800&q=80";

export default function ContactCentrePage() {
  const { departments } = useContent();
  return (
    <div data-testid="contact-centre-page">
      <section className="relative h-[55vh] min-h-[420px] -mt-24 flex items-end overflow-hidden">
        <img src={HERO} alt="Contact centre" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight to-midnight/30" />
        <div className="relative container-edge pb-14 text-linen">
          <div className="overline text-ochre mb-4">Four UK desks</div>
          <h1 className="editorial-h1 max-w-3xl">Contact Centre.</h1>
          <div className="mt-6 flex items-center gap-3 text-linen/80">
            <Clock className="w-5 h-5 stroke-1 text-ochre" />
            <span className="text-sm">{COMPANY.hours}</span>
          </div>
        </div>
      </section>

      <section className="section-pad bg-linen">
        <div className="container-edge">
          <SectionHeading
            overline="Department directory"
            title="Four desks, one promise."
            lede="Trained, UK-based airline professionals answering calls and emails throughout our operating hours."
          />

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
            {departments.map((d) => (
              <Reveal key={d.id} className="bg-linen p-10 md:p-12" data-testid={`dept-card-${d.id}`}>
                <div className="overline text-terracotta">{d.name}</div>
                <p className="text-sm text-muted2 mt-4 leading-relaxed">{d.description}</p>

                <div className="mt-8 space-y-4">
                  <a
                    href={`tel:${d.phone}`}
                    className="flex items-center gap-4 group"
                    data-testid={`dept-phone-${d.id}`}
                  >
                    <span className="w-10 h-10 grid place-items-center border border-ink/15 group-hover:border-terracotta group-hover:bg-terracotta transition-colors">
                      <Phone className="w-4 h-4 stroke-1 group-hover:text-white" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted2">Phone (UK)</div>
                      <div className="font-serif text-2xl group-hover:text-terracotta transition-colors">{d.phoneIntl}</div>
                    </div>
                  </a>
                  <a
                    href={`mailto:${d.email}`}
                    className="flex items-center gap-4 group"
                    data-testid={`dept-email-${d.id}`}
                  >
                    <span className="w-10 h-10 grid place-items-center border border-ink/15 group-hover:border-terracotta group-hover:bg-terracotta transition-colors">
                      <Mail className="w-4 h-4 stroke-1 group-hover:text-white" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-muted2">Email</div>
                      <div className="text-sm group-hover:text-terracotta transition-colors">{d.email}</div>
                    </div>
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Operating hours stripe */}
      <section className="bg-midnight text-linen py-14">
        <div className="container-edge flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="overline text-ochre">Operating hours</div>
            <h3 className="editorial-h3 text-linen mt-2">{COMPANY.hours}</h3>
          </div>
          <a href={`tel:${departments[0].phone}`} className="btn-accent" data-testid="cc-call-now">
            Call us now <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Enquiry */}
      <section className="section-pad bg-sand">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5 md:sticky md:top-28">
            <div className="overline text-terracotta mb-4">Get in touch</div>
            <h2 className="editorial-h2">Send the contact centre a message.</h2>
            <p className="mt-6 text-muted2 leading-relaxed">
              Our UK team responds within one business day. For urgent assistance during operating hours please call the relevant department directly.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              {departments.slice(0, 3).map((d) => (
                <div key={d.id} className="flex items-center justify-between border-b border-ink/10 pb-2">
                  <span className="text-muted2">{d.name}</span>
                  <a href={`tel:${d.phone}`} className="hover:text-terracotta tabular-nums">{d.phoneIntl}</a>
                </div>
              ))}
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <EnquiryForm
              defaultType="general"
              title="How can we help?"
              subtitle="Tell us a little about your enquiry and the relevant desk will pick it up."
              testIdPrefix="cc-enquiry"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
